'use client'

import {
  AlertCircle,
  Bell,
  CheckCircle,
  Inbox,
  Info,
  Settings,
  UserPlus,
  X,
  XCircle,
} from 'lucide-react'
import { useState, useEffect, useRef, useMemo, type RefObject } from 'react'
import {
  useNotificationStore,
  type Notification as NotificationItem,
} from '@/lib/notifications'
import { useRouter } from '@/i18n/navigation'
import { api } from '@/lib/api'
import { InboxModal } from './InboxModal'

// Add imports for apiRequest and toast store
import { apiRequest } from '@/lib/api/utils/requests'
import { useToastNotificationStore } from '@/lib/toast-notifications'
import { useOnClickOutside } from 'usehooks-ts'
import { useAgentAuth } from '@/stores/auth'
import { useTeamsStore } from '@/stores/teams'
import { normalizeTeamIds } from '@/lib/utils/teams'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'

export function NotificationCenter() {
  const ref = useRef<HTMLDivElement>(null)

  const {
    notifications,
    unreadCount,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    dismissNotification,
  } = useNotificationStore()

  const router = useRouter()
  const { user } = useAgentAuth()
  const { teamFilter } = useTeamsStore()

  const addToast = useToastNotificationStore(state => state.addToast)

  // Derive agent teams from teamFilter and user's teamIds
  const agentTeamIds = useMemo(
    () => normalizeTeamIds(user?.teamIds, user?.primaryTeamId),
    [user]
  )

  const agentTeams = useMemo(() => {
    const availableTeams = teamFilter.availableTeams ?? []
    return availableTeams.filter(team => agentTeamIds.includes(team.id))
  }, [teamFilter.availableTeams, agentTeamIds])

  const [isOpen, setIsOpen] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showInboxModal, setShowInboxModal] = useState(false)
  const [soundEnabled, setSoundEnabled] = useState(
    useNotificationStore.getState().soundEnabled
  )
  const [browserNotificationsEnabled, setBrowserNotificationsEnabled] =
    useState(useNotificationStore.getState().browserNotificationsEnabled)
  const isLoading = false
  const [actionLoading, setActionLoading] = useState<number | null>(null)

  // Team selection modal state
  const [showTeamSelectionModal, setShowTeamSelectionModal] = useState(false)
  const [selectedTeamForAccept, setSelectedTeamForAccept] = useState<
    number | null
  >(null)
  const [pendingAcceptNotification, setPendingAcceptNotification] =
    useState<NotificationItem | null>(null)
  const [pendingAcceptAction, setPendingAcceptAction] = useState<any>(null)

  // Sync settings with store
  useEffect(() => {
    const unsubscribe = useNotificationStore.subscribe(state => {
      setSoundEnabled(state.soundEnabled)
      setBrowserNotificationsEnabled(state.browserNotificationsEnabled)
    })
    return unsubscribe
  }, [])

  // Update store when settings change
  const handleSoundToggle = (enabled: boolean) => {
    setSoundEnabled(enabled)
    useNotificationStore.getState().setSoundEnabled(enabled)
  }

  const handleBrowserPermissionToggle = async () => {
    if (!browserNotificationsEnabled) {
      const granted = await useNotificationStore
        .getState()
        .requestBrowserPermission()
      if (!granted) {
        // Replace alert with toast
        addToast({
          type: 'warning',
          title: 'Permission Denied',
          message:
            'Browser notifications were denied. Enable them in your browser settings.',
          urgent: false,
        })
      }
    } else {
      useNotificationStore.getState().setBrowserNotificationsEnabled(false)
    }
  }

  // Fetch unread notifications and count on mount
  useEffect(() => {
    fetchNotifications({
      includeRead: true,
      includeDismissed: true,
      limit: 50,
      offset: 0,
    })
    fetchUnreadCount()
  }, [fetchNotifications, fetchUnreadCount])

  // Fetch unread notifications when opened
  useEffect(() => {
    if (isOpen) {
      fetchNotifications({
        includeRead: true,
        includeDismissed: true,
        limit: 50,
        offset: 0,
      })
      fetchUnreadCount()
    }
  }, [isOpen, fetchNotifications, fetchUnreadCount])

  // Poll for new unread notifications every 30 seconds
  useEffect(() => {
    if (showInboxModal) return

    const interval = setInterval(() => {
      fetchNotifications({
        includeRead: true,
        includeDismissed: true,
        limit: 50,
        offset: 0,
      })
      fetchUnreadCount()
    }, 30000)

    return () => clearInterval(interval)
  }, [fetchNotifications, fetchUnreadCount, showInboxModal])

  // Show only unread and undismissed notifications in the bell dropdown
  const displayedNotifications = notifications.filter(
    n => !n.isRead && !n.isDismissed
  )

  const handleToggle = () => {
    setIsOpen(!isOpen)
  }

  useOnClickOutside(ref as RefObject<HTMLDivElement>, () => {
    setIsOpen(false)
  })

  const handleNotificationClick = async (notification: NotificationItem) => {
    // Mark as read and close panel
    if (!notification.isRead) {
      await markAsRead(notification.id)
      await fetchNotifications({ includeRead: true, includeDismissed: true })
      await fetchUnreadCount()
    }
    setIsOpen(false)
  }

  const handleOpenInbox = () => {
    setIsOpen(false)
    setShowInboxModal(true)
  }

  const handleActionClick = async (
    notification: NotificationItem,
    action: {
      type: string
      label?: string
      url?: string
      onSuccess?: { navigate?: string }
      // Add apiCall metadata support
      apiCall?: {
        method: string
        endpoint: string
        payload?: any
        onSuccess?: { navigate?: string }
      }
    }
  ) => {
    if (actionLoading) return // Prevent multiple clicks

    try {
      setActionLoading(notification.id)

      if (action.type === 'accept') {
        const sessionId = notification.sessionId

        // Extract handoverRequestId from notification (top-level field, not in metadata)
        const handoverRequestId = notification.handoverRequestId

        if (!handoverRequestId) {
          addToast({
            type: 'error',
            title: 'Missing Request ID',
            message: 'Cannot accept: No handover request ID found.',
            urgent: false,
          })
          setActionLoading(null)
          return
        }

        // Extract teamId from notification metadata (if request has specific team)
        const requestTeamId = (notification.metadata as any)?.teamId
        const agentTeamIds = user?.teamIds ?? []

        // Check if this is a broadcast handover (no team assigned) and agent has multiple teams
        // Only show modal if teamId is null/undefined AND agent has multiple teams
        const isBroadcastHandover = requestTeamId == null // Use == to catch both null and undefined

        if (isBroadcastHandover && agentTeamIds.length > 1) {
          // Show team selection modal
          setPendingAcceptNotification(notification)
          setPendingAcceptAction(action)
          setSelectedTeamForAccept(agentTeamIds[0])
          setShowTeamSelectionModal(true)
          setActionLoading(null)
          return
        }

        // Direct accept: use request's teamId if exists, or agent's first team
        const teamIdToUse = requestTeamId || agentTeamIds[0] || undefined

        const response = await api.agent.respondToHandoverAssignment(
          handoverRequestId,
          {
            response: 'accepted',
            reason: '',
            teamId: teamIdToUse,
          }
        )

        if (response?.success) {
          await markAsRead(notification.id)

          // Show success toast
          addToast({
            type: 'success',
            title: 'Handover Accepted',
            message: sessionId
              ? `You are now assigned to session ${String(sessionId).slice(-8)}`
              : 'Handover accepted successfully',
            urgent: false,
          })

          // Navigate
          const navigateTo =
            action.onSuccess?.navigate || action.apiCall?.onSuccess?.navigate
          if (navigateTo) {
            router.push(navigateTo)
          } else if (sessionId) {
            const { buildAgentDashboardChatUrl } = await import('@/lib/utils')
            const fallbackUrl = buildAgentDashboardChatUrl(sessionId)
            router.push(fallbackUrl)
          }

          // Refresh notifications
          await fetchNotifications({
            includeRead: true,
            includeDismissed: true,
          })
          await fetchUnreadCount()
        } else {
          const errMsg = response?.error || 'Handover acceptance failed'

          // Show error toast with appropriate title
          const toastTitle = errMsg.includes('already accepted')
            ? 'Already Accepted'
            : 'Acceptance Failed'

          addToast({
            type: 'warning',
            title: toastTitle,
            message: errMsg,
            urgent: false,
          })

          // Refresh notifications to get updated state from server
          await fetchNotifications({
            includeRead: true,
            includeDismissed: true,
          })
          await fetchUnreadCount()
        }
      } else if (action.type === 'view' && action.url) {
        console.log('👁️ [DEBUG] Handling view action with URL:', action.url)
        // Handle View action
        if (action.url.startsWith('/')) {
          router.push(action.url)
        } else {
          window.location.href = action.url
        }
      }
    } catch (err: any) {
      const errMsg =
        err?.message || 'Failed to execute action. Please try again.'
      addToast({
        type: 'error',
        title: 'Action Error',
        message: errMsg,
        urgent: false,
      })
    } finally {
      setActionLoading(null)
    }
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'handover_requested':
      case 'session_transferred':
        return <UserPlus className="h-5 w-5 text-blue-500" />
      case 'session_completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'system_alert':
      case 'urgent_notification':
        return <AlertCircle className="h-5 w-5 text-red-500" />
      case 'agent_mention':
        return <Info className="h-5 w-5 text-purple-500" />
      default:
        return <Info className="h-5 w-5 text-blue-500" />
    }
  }

  const getNotificationBgColor = (priority: string, isRead: boolean) => {
    if (isRead) return 'bg-white'

    switch (priority) {
      case 'urgent':
        return 'bg-red-50 border-l-4 border-l-red-500'
      case 'high':
        return 'bg-orange-50 border-l-4 border-l-orange-500'
      case 'normal':
        return 'bg-blue-50'
      case 'low':
        return 'bg-gray-50'
      default:
        return 'bg-blue-50'
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="relative" ref={ref}>
      {/* Notification Bell */}
      <button
        onClick={handleToggle}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
        title="Notifications"
      >
        <Bell className="h-6 w-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Notifications
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
                title="Settings"
              >
                <Settings className="h-4 w-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Notification Settings
              </h4>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={soundEnabled}
                    onChange={e => handleSoundToggle(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Sound alerts
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={browserNotificationsEnabled}
                    onChange={handleBrowserPermissionToggle}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Browser notifications
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Actions Header */}
          {displayedNotifications.length > 0 && (
            <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
              <span className="text-sm text-gray-600">
                {displayedNotifications.length} unread notification
                {displayedNotifications.length !== 1 ? 's' : ''}
              </span>
              <button
                onClick={markAllAsRead}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                disabled={isLoading}
              >
                Mark all read
              </button>
            </div>
          )}

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading && displayedNotifications.length === 0 ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />
                <p className="mt-2 text-sm text-gray-500">Loading...</p>
              </div>
            ) : displayedNotifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No unread notifications
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {"You're all caught up!"}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {displayedNotifications.map(notification => {
                  const actions =
                    (notification.metadata?.actions as any[]) || []

                  return (
                    <div
                      key={notification.id}
                      className={`p-4 hover:bg-gray-50 transition-colors ${getNotificationBgColor(
                        notification.priority,
                        notification.isRead
                      )}`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="shrink-0">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                {notification.title}
                                {notification.priority === 'urgent' && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                    Urgent
                                  </span>
                                )}
                                {notification.priority === 'high' && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                    High Priority
                                  </span>
                                )}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {formatTimestamp(notification.createdAt)}
                              </p>

                              {/* Action Buttons */}
                              {actions.length > 0 && (
                                <div className="mt-3 flex items-center space-x-2">
                                  {actions.map((action, idx) => {
                                    if (action.type === 'accept') {
                                      return (
                                        <button
                                          key={idx}
                                          onClick={() =>
                                            handleActionClick(
                                              notification,
                                              action
                                            )
                                          }
                                          disabled={
                                            actionLoading === notification.id
                                          }
                                          className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {actionLoading === notification.id
                                            ? 'Processing...'
                                            : action.label || 'Accept'}
                                        </button>
                                      )
                                    }

                                    if (action.type === 'view') {
                                      return (
                                        <button
                                          key={idx}
                                          onClick={() =>
                                            handleActionClick(
                                              notification,
                                              action
                                            )
                                          }
                                          className="px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 border border-blue-600 rounded"
                                        >
                                          {action.label || 'View'}
                                        </button>
                                      )
                                    }

                                    return null
                                  })}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={e => {
                                e.stopPropagation()
                                // Mark as read instead of dismiss
                                // This keeps the notification in inbox
                                markAsRead(notification.id)
                              }}
                              className="ml-2 shrink-0 text-gray-400 hover:text-gray-600"
                              title="Mark as read"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Inbox Link Footer */}
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleOpenInbox}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Inbox className="h-4 w-4" />
              View All Notifications in Inbox
            </button>
          </div>
        </div>
      )}

      {/* Inbox Modal */}
      <InboxModal
        isOpen={showInboxModal}
        onClose={() => setShowInboxModal(false)}
      />

      {/* Team Selection Modal */}
      <Dialog
        open={showTeamSelectionModal}
        onOpenChange={setShowTeamSelectionModal}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Select Team for Handover</DialogTitle>
            <DialogDescription>
              This is a broadcast handover (no specific team assigned). Please
              select which team you want to accept this handover under:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="team-select">Team</Label>
              <Select
                value={selectedTeamForAccept?.toString() || ''}
                onValueChange={value => setSelectedTeamForAccept(Number(value))}
              >
                <SelectTrigger id="team-select">
                  <SelectValue placeholder="Select a team" />
                </SelectTrigger>
                <SelectContent>
                  {agentTeams.map(team => (
                    <SelectItem key={team.id} value={team.id.toString()}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {pendingAcceptNotification && (
              <div className="mt-4 p-3 bg-gray-50 rounded-md space-y-2">
                <div className="text-xs text-gray-500">Session</div>
                <div className="text-sm font-medium">
                  {pendingAcceptNotification.sessionId?.slice(-8) || 'Unknown'}
                </div>
                {pendingAcceptNotification.title && (
                  <div className="text-xs text-gray-600">
                    {pendingAcceptNotification.title}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowTeamSelectionModal(false)
                setPendingAcceptNotification(null)
                setPendingAcceptAction(null)
                setSelectedTeamForAccept(null)
              }}
              disabled={actionLoading !== null}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!pendingAcceptNotification || !selectedTeamForAccept) return

                setActionLoading(pendingAcceptNotification.id)

                try {
                  const handoverRequestId =
                    pendingAcceptNotification.handoverRequestId ??
                    (pendingAcceptNotification.metadata as any)
                      ?.handoverRequestId ??
                    (pendingAcceptNotification.metadata as any)?.requestId

                  const response = await api.agent.respondToHandoverAssignment(
                    handoverRequestId,
                    {
                      response: 'accepted',
                      reason: '',
                      teamId: selectedTeamForAccept,
                    }
                  )

                  if (response?.success) {
                    await markAsRead(pendingAcceptNotification.id)

                    addToast({
                      type: 'success',
                      title: 'Handover Accepted',
                      message: pendingAcceptNotification.sessionId
                        ? `You are now assigned to session ${String(pendingAcceptNotification.sessionId).slice(-8)}`
                        : 'Handover accepted successfully',
                      urgent: false,
                    })

                    // Navigate
                    const navigateTo =
                      pendingAcceptAction?.onSuccess?.navigate ||
                      pendingAcceptAction?.apiCall?.onSuccess?.navigate
                    if (navigateTo) {
                      router.push(navigateTo)
                    } else if (pendingAcceptNotification.sessionId) {
                      const { buildAgentDashboardChatUrl } = await import(
                        '@/lib/utils'
                      )
                      const fallbackUrl = buildAgentDashboardChatUrl(
                        pendingAcceptNotification.sessionId
                      )
                      router.push(fallbackUrl)
                    }

                    // Close modal
                    setShowTeamSelectionModal(false)
                    setPendingAcceptNotification(null)
                    setPendingAcceptAction(null)
                    setSelectedTeamForAccept(null)

                    // Refresh notifications
                    await fetchNotifications({
                      includeRead: true,
                      includeDismissed: true,
                    })
                    await fetchUnreadCount()
                  } else {
                    const errMsg =
                      response?.error || 'Handover acceptance failed'

                    addToast({
                      type: 'warning',
                      title: 'Acceptance Failed',
                      message: errMsg,
                      urgent: false,
                    })

                    // Refresh notifications
                    await fetchNotifications({
                      includeRead: true,
                      includeDismissed: true,
                    })
                    await fetchUnreadCount()
                  }
                } catch (err: any) {
                  addToast({
                    type: 'error',
                    title: 'Action Error',
                    message: err?.message || 'Failed to accept handover',
                    urgent: false,
                  })
                } finally {
                  setActionLoading(null)
                }
              }}
              disabled={actionLoading !== null || !selectedTeamForAccept}
            >
              {actionLoading !== null ? 'Accepting...' : 'Accept Handover'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
